import React, { useEffect, useMemo, useRef, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { getTemporadas } from "../../api/Temporadas/temporadaApi";
import { getCustomers } from "../../api/Customer/customerApi";
import { createSkillAgentMoodboardFromMeeting } from "../../api/Skills/skillAgentMoodboardsApi";
import { sortTemporadasByYearThenAlpha } from "./meetingTemporadaSort";
import {
  matchClientOption,
  optionsFromCustomers,
} from "./meetingMoodboardClientOptions";

function isImagePhoto(photo) {
  const mime = String(photo?.mimeType || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  if (mime.startsWith("video/")) return false;
  const name = String(
    photo?.originalFileName || photo?.fileUrl || "",
  ).toLowerCase();
  return /\.(jpe?g|png|webp|gif|bmp)$/i.test(name);
}

/**
 * Dialog: create moodboard from selected meeting photos.
 * Cliente = catálogo CRM (dbo.Customers), sin texto libre.
 */
export default function MeetingCreateMoodboardDialog({
  open,
  onClose,
  session,
  selectedPhotos = [],
  onCreated,
}) {
  const { t } = useTranslation();
  const sessionId = Number(session?.id) || 0;
  const openedForRef = useRef(null);
  const catalogsRef = useRef({ temporadas: null, options: null });

  const [catalogsReady, setCatalogsReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [temporadas, setTemporadas] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [titulo, setTitulo] = useState("");
  const [temporada, setTemporada] = useState("");
  const [cliente, setCliente] = useState(null);

  const imagePhotos = useMemo(
    () =>
      (Array.isArray(selectedPhotos) ? selectedPhotos : []).filter(
        isImagePhoto,
      ),
    [selectedPhotos],
  );
  const skippedVideos = Math.max(
    0,
    (selectedPhotos?.length || 0) - imagePhotos.length,
  );

  useEffect(() => {
    if (!open) return undefined;
    if (catalogsRef.current.temporadas && catalogsRef.current.options) {
      setTemporadas(catalogsRef.current.temporadas);
      setCustomerOptions(catalogsRef.current.options);
      setCatalogsReady(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [temps, customersData] = await Promise.all([
          getTemporadas(),
          getCustomers(),
        ]);
        if (cancelled) return;
        const tempList = sortTemporadasByYearThenAlpha(
          Array.isArray(temps) ? temps : [],
        );
        const options = optionsFromCustomers(
          Array.isArray(customersData) ? customersData : [],
        );
        catalogsRef.current = { temporadas: tempList, options };
        setTemporadas(tempList);
        setCustomerOptions(options);
        setCatalogsReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error || t("Meetings.moodboardLoadError"),
          );
          setCatalogsReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  useEffect(() => {
    if (!open || !sessionId) return;
    if (openedForRef.current === sessionId) return;
    openedForRef.current = sessionId;
    setError("");
    setSaving(false);
    setTemporada("");
    const customerName = String(session?.customerName || "").trim();
    const meetingLabel = String(
      session?.title || customerName || "Reunión",
    ).trim();
    setTitulo(`Moodboard · ${meetingLabel}`.slice(0, 240));
    const options = catalogsRef.current.options || customerOptions;
    setCliente(matchClientOption(session, options));
  }, [open, sessionId, session, customerOptions]);

  useEffect(() => {
    if (!open) openedForRef.current = null;
  }, [open]);

  useEffect(() => {
    if (!open || !catalogsReady || cliente || !customerOptions.length) return;
    setCliente(matchClientOption(session, customerOptions));
  }, [open, catalogsReady, customerOptions, cliente, session]);

  const handleCreate = async () => {
    if (!imagePhotos.length) {
      setError(t("Meetings.moodboardNeedPhotos"));
      return;
    }
    if (!String(titulo || "").trim()) {
      setError(t("Meetings.moodboardNeedTitle"));
      return;
    }
    if (!temporada) {
      setError(t("Meetings.moodboardNeedSeason"));
      return;
    }
    if (!cliente?.id) {
      setError(t("Meetings.moodboardNeedClient"));
      return;
    }
    const allowed = customerOptions.some(
      (o) => Number(o.id) === Number(cliente.id),
    );
    if (!allowed) {
      setError(t("Meetings.moodboardNeedClient"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const created = await createSkillAgentMoodboardFromMeeting({
        sessionId,
        photoIds: imagePhotos.map((p) => Number(p.id)).filter((n) => n > 0),
        Titulo: String(titulo).trim(),
        CategoryLabel: String(titulo).trim(),
        Temporada: temporada,
        CustomerId: Number(cliente.id),
      });
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.error || t("Meetings.moodboardCreateError"),
      );
    } finally {
      setSaving(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: (th) => alpha(th.palette.background.default, 0.55),
    },
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose?.()}
      maxWidth="xs"
      fullWidth
      keepMounted={false}
      transitionDuration={160}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <Box
        sx={{
          px: 2.75,
          pt: 2.5,
          pb: 1.75,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: (th) =>
            `linear-gradient(135deg, ${alpha(th.palette.primary.main, 0.1)} 0%, ${alpha(
              th.palette.info.main,
              0.06,
            )} 100%)`,
        }}
      >
        <Typography variant="overline" sx={{ opacity: 0.7, letterSpacing: 1 }}>
          {t("Meetings.title")}
        </Typography>
        <Typography
          variant="h6"
          fontWeight={800}
          sx={{ lineHeight: 1.2, mt: 0.25 }}
        >
          {t("Meetings.moodboardTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {t("Meetings.moodboardBody", { count: imagePhotos.length })}
          {skippedVideos > 0
            ? ` ${t("Meetings.moodboardSkipVideos", { count: skippedVideos })}`
            : ""}
        </Typography>
      </Box>

      <Box sx={{ px: 2.75, pt: 2, pb: 1, minHeight: 188 }}>
        <Stack spacing={2}>
          {error ? (
            <Typography variant="body2" color="error" fontWeight={700}>
              {error}
            </Typography>
          ) : null}
          <TextField
            label={t("Meetings.moodboardName")}
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            fullWidth
            size="small"
            disabled={saving}
            sx={fieldSx}
          />
          <FormControl
            fullWidth
            size="small"
            disabled={saving || !catalogsReady}
          >
            <InputLabel id="meeting-mb-season" shrink>
              {t("Meetings.moodboardSeason")}
            </InputLabel>
            <Select
              labelId="meeting-mb-season"
              label={t("Meetings.moodboardSeason")}
              value={temporada}
              displayEmpty
              notched
              onChange={(e) => setTemporada(e.target.value)}
              sx={{
                borderRadius: 2,
                bgcolor: (th) => alpha(th.palette.background.default, 0.55),
              }}
            >
              <MenuItem value="" disabled>
                {catalogsReady
                  ? t("Meetings.moodboardSeason")
                  : t("Meetings.moodboardLoadingCatalog", {
                      defaultValue: "Cargando…",
                    })}
              </MenuItem>
              {temporadas.map((temp) => {
                const label = String(temp.TEMPORADA || "").trim();
                if (!label) return null;
                return (
                  <MenuItem key={temp.ID_TEMPORADA || label} value={label}>
                    {label}
                    {temp.ABREVIATURA ? ` (${temp.ABREVIATURA})` : ""}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <Autocomplete
            options={customerOptions}
            value={cliente}
            onChange={(_, value) => setCliente(value)}
            getOptionLabel={(opt) => opt?.label || ""}
            isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
            disabled={saving || !catalogsReady}
            loading={!catalogsReady}
            autoHighlight
            clearOnBlur
            handleHomeEndKeys
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t("Meetings.moodboardClient")}
                placeholder={t("Meetings.moodboardClientHint")}
                InputLabelProps={{ shrink: true }}
                sx={fieldSx}
              />
            )}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          px: 2.75,
          pb: 2.25,
          pt: 1.25,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: (th) => alpha(th.palette.text.primary, 0.02),
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => onClose?.()}
          disabled={saving}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
        >
          {t("Meetings.cancel", { defaultValue: "Cancelar" })}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={saving || !imagePhotos.length}
          onClick={handleCreate}
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : null
          }
          sx={{
            textTransform: "none",
            fontWeight: 800,
            borderRadius: 2,
            px: 2,
            color: "primary.contrastText",
          }}
        >
          {t("Meetings.moodboardConfirm")}
        </Button>
      </Box>
    </Dialog>
  );
}
